import { createSlice } from "@reduxjs/toolkit";
import {
  getWeeklyUploadedByChannel,
  getWeeklyUploaded,
  getWeeklyNewSupliers,
  getWeeklyTotalOrderAmount,
  getWeeklyTotalSupplierCancelledOrders,
  getWeeklyTotalCustomerCancelledOrders,
  getWeeklyTotalPurchases,
  getUserStats,
  getSubscriptionStats,
  getWeeklyRefunds,
  getTopMembersPurchases,
  getTopProductMargins,
  getTopSellers,
  getTopSellerSuppliers,
  getWeeklyGeneralCustomerOrderCounts,
  getWeeklyGeneralSupplierOrderCounts,
  getGeneralSupplierOrderCounts,
  getInventoryHistory,
  getWeeklyProfitableProducts,
  getMonthlyUploaded,
  getMonthlyUploadedByChannel,
  getMonthlyNewSuppliers,
  getMonthlyGeneralSupplierOrderCounts,
  getMonthlyGeneralCustomerOrderCounts,
  getMonthlyRefunds,
  getMonthlyProfitableProducts,
} from "./thunk"; 

export const initialState = {
  weeklyUploaded: null,
  weeklyUploadedByChannel: null,
  weeklyNewSupliers: null,
  weeklyTotalOrderAmount: null,
  weeklyTotalSupplierCancelledOrders: null,
  weeklyGeneralCustomerOrderCounts: null,
  weeklyGeneralSupplierOrderCounts: null,
  generalSupplierOrderCounts: null,
  userStats: null,
  subscriptionStats: null,
  weeklyTotalMemberPurchases: null,
  weeklyRefunds: null,
  topSellers: null,
  topSellersSuppliers: null,
  topProductMargins: null,
  topMemberPurchases: null,
  weeklyProfitableProducts: null,
  error: {},
};

const normalizeUserStats = (userStats = {}) => {
  // Resolve base values, supporting both legacy field names and new /io/Reports/UsersStatsByRange fields (inr_*)
  const resolveSubscriptionTotal = ({
    totalValue,
    legacyInrValue,
    yearlyValue,
    monthlyValue,
    fallbackValue,
  }) => {
    if (totalValue !== undefined && totalValue !== null) return totalValue;
    if (legacyInrValue !== undefined && legacyInrValue !== null) return legacyInrValue;

    const hasExpandedBreakdown =
      yearlyValue !== undefined || monthlyValue !== undefined;

    if (hasExpandedBreakdown) {
      return Number(yearlyValue || 0) + Number(monthlyValue || 0);
    }

    return fallbackValue ?? 0;
  };

  const total_logins =
    userStats.total_logins ??
    userStats.inr_totalLogin ??
    userStats.total_logins_last_30 ??
    0;
  const total_logins_bpa =
    userStats.total_logins_bpa ?? userStats.inr_bpaLogin ?? 0;
  const total_new_subscriptions_yearly =
    userStats.total_new_subscriptions_yearly ??
    userStats.inr_subsYearNew ??
    0;
  const total_new_subscriptions_monthly =
    userStats.total_new_subscriptions_monthly ??
    userStats.inr_subsMonthNew ??
    0;
  const total_ren_subscriptions_yearly =
    userStats.total_ren_subscriptions_yearly ??
    userStats.inr_subsYearRenew ??
    0;
  const total_ren_subscriptions_monthly =
    userStats.total_ren_subscriptions_monthly ??
    userStats.inr_subsMonthRenew ??
    0;
  const total_del_subscriptions_yearly =
    userStats.total_del_subscriptions_yearly ??
    userStats.inr_subsYearCancel ??
    0;
  const total_del_subscriptions_monthly =
    userStats.total_del_subscriptions_monthly ??
    userStats.inr_subsMonthCancel ??
    0;
  const total_new_subscriptions = resolveSubscriptionTotal({
    totalValue: userStats.total_new_subscriptions,
    legacyInrValue: userStats.inr_subsNew,
    yearlyValue: userStats.total_new_subscriptions_yearly ?? userStats.inr_subsYearNew,
    monthlyValue:
      userStats.total_new_subscriptions_monthly ?? userStats.inr_subsMonthNew,
    fallbackValue: userStats.total_new_subscriptions_30,
  });
  const total_ren_subscriptions = resolveSubscriptionTotal({
    totalValue: userStats.total_ren_subscriptions,
    legacyInrValue: userStats.inr_subsRenew,
    yearlyValue:
      userStats.total_ren_subscriptions_yearly ?? userStats.inr_subsYearRenew,
    monthlyValue:
      userStats.total_ren_subscriptions_monthly ?? userStats.inr_subsMonthRenew,
    fallbackValue: userStats.total_ren_subscriptions_30,
  });
  const total_del_subscriptions = resolveSubscriptionTotal({
    totalValue: userStats.total_del_subscriptions,
    legacyInrValue: userStats.inr_subsCancel,
    yearlyValue:
      userStats.total_del_subscriptions_yearly ?? userStats.inr_subsYearCancel,
    monthlyValue:
      userStats.total_del_subscriptions_monthly ?? userStats.inr_subsMonthCancel,
    fallbackValue: userStats.total_del_subscriptions_30,
  });

  return {
    ...userStats,
    total_logins,
    total_logins_bpa,
    total_new_subscriptions,
    total_new_subscriptions_yearly,
    total_new_subscriptions_monthly,
    total_ren_subscriptions,
    total_ren_subscriptions_yearly,
    total_ren_subscriptions_monthly,
    total_del_subscriptions,
    total_del_subscriptions_yearly,
    total_del_subscriptions_monthly,
    // Propagate to _30/_60 variants used by Dashboard component
    total_logins_last_30:
      userStats.total_logins_last_30 ?? total_logins ?? 0,
    total_logins_last_60: userStats.total_logins_last_60 ?? 0,
    total_new_subscriptions_30:
      userStats.total_new_subscriptions_30 ??
      total_new_subscriptions ??
      0,
    total_new_subscriptions_60:
      userStats.total_new_subscriptions_60 ?? 0,
    total_del_subscriptions_30:
      userStats.total_del_subscriptions_30 ??
      total_del_subscriptions ??
      0,
    total_del_subscriptions_60: userStats.total_del_subscriptions_60 ?? 0,
    total_ren_subscriptions_30:
      userStats.total_ren_subscriptions_30 ??
      total_ren_subscriptions ??
      0,
    total_ren_subscriptions_60: userStats.total_ren_subscriptions_60 ?? 0,
    total_new_subscriptions_bpa: userStats.total_new_subscriptions_bpa ?? 0,
    total_del_subscriptions_bpa: userStats.total_del_subscriptions_bpa ?? 0,
    total_ren_subscriptions_bpa: userStats.total_ren_subscriptions_bpa ?? 0,
  };
};

const DashboardSlice = createSlice({
  name: "Dashboard",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // For weekly uploaded
    // This will be used to get the weekly uploaded data
    // and will be used in the dashboard to show the weekly uploaded data

    builder.addCase(getWeeklyUploaded.pending, (state, action) => {
      state.weeklyUploaded = null;
      state.error = {};
    });
    builder.addCase(getWeeklyUploaded.fulfilled, (state, action) => {
      state.weeklyUploaded = action.payload.data;
    });
    builder.addCase(getWeeklyUploaded.rejected, (state, action) => {
      state.error = action.payload.message || null;
    });

    // For weekly uploaded by channel
    // This will be used to get the weekly uploaded data by channel
    // and will be used in the dashboard to show the weekly uploaded data by channel
    builder.addCase(getWeeklyUploadedByChannel.pending, (state, action) => {
      state.weeklyUploadedByChannel = null;
      state.error = {};
    });
    builder.addCase(getWeeklyUploadedByChannel.fulfilled, (state, action) => {
      state.weeklyUploadedByChannel = action.payload.data;
    });
    builder.addCase(getWeeklyUploadedByChannel.rejected, (state, action) => {
      state.error = action.payload.message || null;
    });

    // For weekly new suppliers
    // This will be used to get the weekly new suppliers data
    // and will be used in the dashboard to show the weekly new suppliers data
    builder.addCase(getWeeklyNewSupliers.pending, (state, action) => {
      state.weeklyNewSupliers = null;
      state.error = {};
    });
    builder.addCase(getWeeklyNewSupliers.fulfilled, (state, action) => {
      state.weeklyNewSupliers = action.payload.data;
    });
    builder.addCase(getWeeklyNewSupliers.rejected, (state, action) => {
      state.error = action.payload.message || null;
    });
    // For weekly total order amount
    // This will be used to get the weekly total order amount data
    // and will be used in the dashboard to show the weekly total order amount data
    builder.addCase(getWeeklyTotalOrderAmount.pending, (state, action) => {
      state.weeklyTotalOrderAmount = null;
      state.error = {};
    });
    builder.addCase(getWeeklyTotalOrderAmount.fulfilled, (state, action) => {
      state.weeklyTotalOrderAmount = action.payload.data;
    });
    builder.addCase(getWeeklyTotalOrderAmount.rejected, (state, action) => {
      state.error = action.payload.message || null;
    });

    // For weekly total supplier cancelled orders
    // This will be used to get the weekly total supplier cancelled orders data
    // and will be used in the dashboard to show the weekly total supplier cancelled orders data
    builder.addCase(
      getWeeklyTotalSupplierCancelledOrders.pending,
      (state, action) => {
        state.weeklyTotalSupplierCancelledOrders = null;
        state.error = {};
      }
    );
    builder.addCase(
      getWeeklyTotalSupplierCancelledOrders.fulfilled,
      (state, action) => {
        state.weeklyTotalSupplierCancelledOrders = action.payload.data;
      }
    );
    builder.addCase(
      getWeeklyTotalSupplierCancelledOrders.rejected,
      (state, action) => {
        state.error = action.payload.message || null;
      }
    );

    // For weekly total customer cancelled orders
    // This will be used to get the weekly total customer cancelled orders data
    // and will be used in the dashboard to show the weekly total customer cancelled orders data
    builder.addCase(
      getWeeklyTotalCustomerCancelledOrders.pending,
      (state, action) => {
        state.weeklyTotalCustomerCancelledOrders = null;
        state.error = {};
      }
    );
    builder.addCase(
      getWeeklyTotalCustomerCancelledOrders.fulfilled,
      (state, action) => {
        state.weeklyTotalCustomerCancelledOrders = action.payload.data;
      }
    );
    builder.addCase(
      getWeeklyTotalCustomerCancelledOrders.rejected,
      (state, action) => {
        state.error = action.payload.message || null;
      }
    );

    // For user stats
    // This will be used to get the user stats data
    builder.addCase(getUserStats.pending, (state, action) => {
      if (!action.meta.arg) state.userStats = null;
      state.error = {};
    });
    builder.addCase(getUserStats.fulfilled, (state, action) => {
      const msg = action.payload?.message;
      if (msg && typeof msg === "string" && msg.indexOf("http") !== -1) {
        window.open(msg, "_blank");
      } else {
        state.userStats = normalizeUserStats(action.payload?.data);
      }
    });
    builder.addCase(getUserStats.rejected, (state, action) => {
      state.error = action.payload?.message || null;
    });

    //for SubscriptionStats
    // This will be used to get the subscription stats data

    builder.addCase(getSubscriptionStats.pending, (state, action) => {
      state.subscriptionStats = null;
      state.error = {};
    });
    builder.addCase(getSubscriptionStats.fulfilled, (state, action) => {
      state.subscriptionStats = action.payload.data;
    });
    builder.addCase(getSubscriptionStats.rejected, (state, action) => {
      state.error = action.payload.message || null;
    });

    //For WeeklyTotalMemberPurchases
    // This will be used to get the weekly total member purchases data
    builder.addCase(getWeeklyTotalPurchases.pending, (state, action) => {
      if (action.meta.arg.download === undefined)
        state.weeklyTotalMemberPurchases = null;
      state.error = {};
    });
    builder.addCase(getWeeklyTotalPurchases.fulfilled, (state, action) => {
      const msg = action.payload?.message;
      if (msg && typeof msg === "string" && msg.indexOf("http") !== -1) {
        window.open(msg, "_blank");
      } else {
        state.weeklyTotalMemberPurchases = action.payload?.data;
      }
    });
    builder.addCase(getWeeklyTotalPurchases.rejected, (state, action) => {
      state.error = action.payload?.message || null;
    });

    //For weeklyRefunds
    // This will be used to get the weekly total member refunds data
    builder.addCase(getWeeklyRefunds.pending, (state, action) => {
      state.weeklyRefunds = null;
      state.error = {};
    });
    builder.addCase(getWeeklyRefunds.fulfilled, (state, action) => {
      state.weeklyRefunds = action.payload.data;
    });
    builder.addCase(getWeeklyRefunds.rejected, (state, action) => {
      state.error = action.payload.message || null;
    });

    //For Top memeber purchases
    builder.addCase(getTopMembersPurchases.pending, (state, action) => {
      state.topMemberPurchases = null;
      state.error = {};
    });
    builder.addCase(getTopMembersPurchases.fulfilled, (state, action) => {
      state.topMemberPurchases = action.payload.data;
    });
    builder.addCase(getTopMembersPurchases.rejected, (state, action) => {
      state.error = action.payload.message || null;
    });

    //For top product margins
    builder.addCase(getTopProductMargins.pending, (state, action) => {
      state.topProductMargins = null;
      state.error = {};
    });
    builder.addCase(getTopProductMargins.fulfilled, (state, action) => {
      state.topProductMargins = action.payload.data;
    });
    builder.addCase(getTopProductMargins.rejected, (state, action) => {
      state.error = action.payload.message || null;
    });

    //Fro top sellers
    builder.addCase(getTopSellers.pending, (state, action) => {
      state.topSellers = null;
      state.error = {};
    });
    builder.addCase(getTopSellers.fulfilled, (state, action) => {
      state.topSellers = action.payload.data;
    });
    builder.addCase(getTopSellers.rejected, (state, action) => {
      state.error = action.payload.message || null;
    });

    //For top seller suppliers
    builder.addCase(getTopSellerSuppliers.pending, (state, action) => {
      state.topSellersSuppliers = null;
      state.error = {};
    });
    builder.addCase(getTopSellerSuppliers.fulfilled, (state, action) => {
      state.topSellersSuppliers = action.payload.data;
    });
    builder.addCase(getTopSellerSuppliers.rejected, (state, action) => {
      state.error = action.payload.message || null;
    });
    // For weekly general customer order counts
    builder.addCase(
      getWeeklyGeneralCustomerOrderCounts.pending,
      (state, action) => {
        if (action.meta.arg.download != true)
          state.weeklyGeneralCustomerOrderCounts = null;
        state.error = {};
      }
    );
    builder.addCase(
      getWeeklyGeneralCustomerOrderCounts.fulfilled,
      (state, action) => {
        const msg = action.payload?.message;
        if (msg && typeof msg === "string" && msg.indexOf("http") !== -1) {
          window.open(msg, "_blank");
        } else {
          state.weeklyGeneralCustomerOrderCounts = action.payload?.data;
        }
      }
    );
    builder.addCase(
      getWeeklyGeneralCustomerOrderCounts.rejected,
      (state, action) => {
        state.error = action.payload?.message || null;
      }
    );
    // For weekly general supplier order counts
    builder.addCase(
      getWeeklyGeneralSupplierOrderCounts.pending,
      (state, action) => {
        if (action.meta.arg.download != true)
          state.weeklyGeneralSupplierOrderCounts = null;
        state.error = {};
      }
    );
    builder.addCase(
      getWeeklyGeneralSupplierOrderCounts.fulfilled,
      (state, action) => {
        const msg = action.payload?.message;
        if (msg && typeof msg === "string" && msg.indexOf("http") !== -1) {
          window.open(msg, "_blank");
        } else {
          state.weeklyGeneralSupplierOrderCounts = action.payload?.data;
        }
      }
    );
    builder.addCase(
      getWeeklyGeneralSupplierOrderCounts.rejected,
      (state, action) => {
        state.error = action.payload?.message || null;
      }
    );
    // For general supplier order counts (non-weekly, aggregated)
    builder.addCase(
      getGeneralSupplierOrderCounts.pending,
      (state, action) => {
        if (action.meta?.arg?.download != true)
          state.generalSupplierOrderCounts = null;
        state.error = {};
      }
    );
    builder.addCase(
      getGeneralSupplierOrderCounts.fulfilled,
      (state, action) => {
        const msg = action.payload?.message || action.payload?.url;
        if (msg && typeof msg === "string" && msg.indexOf("http") !== -1) {
          window.open(msg, "_blank");
        } else {
          state.generalSupplierOrderCounts = action.payload?.data;
        }
      }
    );
    builder.addCase(
      getGeneralSupplierOrderCounts.rejected,
      (state, action) => {
        state.error = action.payload?.message || action.error?.message || null;
      }
    );
    // For inventory history
    builder.addCase(getInventoryHistory.pending, (state, action) => {
      if (action.meta.arg.download != true) state.inventoryHistory = null;
      state.error = {};
    });
    builder.addCase(getInventoryHistory.fulfilled, (state, action) => {
      const msg = action.payload?.message;
      if (msg && typeof msg === "string" && msg.indexOf("http") !== -1) {
        window.open(msg, "_blank");
      } else {
        state.inventoryHistory = action.payload?.data;
      }
    });
    builder.addCase(getInventoryHistory.rejected, (state, action) => {
      state.error = action.payload?.message || null;
    });
    // For weekly profitable products
    builder.addCase(getWeeklyProfitableProducts.pending, (state, action) => {
      if (action.meta.arg.download != true) state.weeklyProfitableProducts = null;
      state.error = {};
    });
    builder.addCase(getWeeklyProfitableProducts.fulfilled, (state, action) => {
      const msg = action.payload?.message || action.payload?.data?.message;
      if (msg && typeof msg === "string" && msg.indexOf("http") !== -1) {
        window.open(msg, "_blank");
      } else {
        const raw = action.payload?.data;
        // API wraps in { success, data: [...] }; unwrap if needed
        state.weeklyProfitableProducts = Array.isArray(raw?.data) ? raw.data : raw;
      }
    });
    builder.addCase(getWeeklyProfitableProducts.rejected, (state, action) => {
      state.error = action.payload?.message || null;
    });
    // Monthly dashboard reducers (reuse same state fields as weekly)
    builder.addCase(getMonthlyUploaded.pending, (state) => {
      state.weeklyUploaded = null;
      state.error = {};
    });
    builder.addCase(getMonthlyUploaded.fulfilled, (state, action) => {
      state.weeklyUploaded = action.payload?.data;
    });
    builder.addCase(getMonthlyUploaded.rejected, (state, action) => {
      state.error = action.payload?.message || null;
    });
    builder.addCase(getMonthlyUploadedByChannel.pending, (state) => {
      state.weeklyUploadedByChannel = null;
      state.error = {};
    });
    builder.addCase(getMonthlyUploadedByChannel.fulfilled, (state, action) => {
      state.weeklyUploadedByChannel = action.payload?.data;
    });
    builder.addCase(getMonthlyUploadedByChannel.rejected, (state, action) => {
      state.error = action.payload?.message || null;
    });
    builder.addCase(getMonthlyNewSuppliers.pending, (state) => {
      state.weeklyNewSupliers = null;
      state.error = {};
    });
    builder.addCase(getMonthlyNewSuppliers.fulfilled, (state, action) => {
      state.weeklyNewSupliers = action.payload?.data;
    });
    builder.addCase(getMonthlyNewSuppliers.rejected, (state, action) => {
      state.error = action.payload?.message || null;
    });
    builder.addCase(
      getMonthlyGeneralSupplierOrderCounts.pending,
      (state, action) => {
        if (action.meta.arg.download != true)
          state.weeklyGeneralSupplierOrderCounts = null;
        state.error = {};
      }
    );
    builder.addCase(
      getMonthlyGeneralSupplierOrderCounts.fulfilled,
      (state, action) => {
        const msg = action.payload?.message;
        if (msg && typeof msg === "string" && msg.indexOf("http") !== -1) {
          window.open(msg, "_blank");
        } else {
          state.weeklyGeneralSupplierOrderCounts = action.payload?.data;
        }
      }
    );
    builder.addCase(
      getMonthlyGeneralSupplierOrderCounts.rejected,
      (state, action) => {
        state.error = action.payload?.message || null;
      }
    );
    builder.addCase(
      getMonthlyGeneralCustomerOrderCounts.pending,
      (state, action) => {
        if (action.meta.arg.download != true)
          state.weeklyGeneralCustomerOrderCounts = null;
        state.error = {};
      }
    );
    builder.addCase(
      getMonthlyGeneralCustomerOrderCounts.fulfilled,
      (state, action) => {
        const msg = action.payload?.message;
        if (msg && typeof msg === "string" && msg.indexOf("http") !== -1) {
          window.open(msg, "_blank");
        } else {
          state.weeklyGeneralCustomerOrderCounts = action.payload?.data;
        }
      }
    );
    builder.addCase(
      getMonthlyGeneralCustomerOrderCounts.rejected,
      (state, action) => {
        state.error = action.payload?.message || null;
      }
    );
    // Monthly refunds (reuse weeklyRefunds state field)
    builder.addCase(getMonthlyRefunds.pending, (state) => {
      state.weeklyRefunds = null;
      state.error = {};
    });
    builder.addCase(getMonthlyRefunds.fulfilled, (state, action) => {
      state.weeklyRefunds = action.payload?.data;
    });
    builder.addCase(getMonthlyRefunds.rejected, (state, action) => {
      state.error = action.payload?.message || null;
    });
    // Monthly profitable products (reuse weeklyProfitableProducts state field)
    builder.addCase(getMonthlyProfitableProducts.pending, (state, action) => {
      if (action.meta.arg.download != true) state.weeklyProfitableProducts = null;
      state.error = {};
    });
    builder.addCase(getMonthlyProfitableProducts.fulfilled, (state, action) => {
      const msg = action.payload?.message || action.payload?.data?.message;
      if (msg && typeof msg === "string" && msg.indexOf("http") !== -1) {
        window.open(msg, "_blank");
      } else {
        const raw = action.payload?.data;
        state.weeklyProfitableProducts = Array.isArray(raw?.data) ? raw.data : raw;
      }
    });
    builder.addCase(getMonthlyProfitableProducts.rejected, (state, action) => {
      state.error = action.payload?.message || null;
    });
  },
});

export default DashboardSlice.reducer;
